import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatINR, formatHour, formatApiError } from "@/lib/format";
import { toast } from "sonner";
import {
  Activity,
  Wrench,
  Armchair,
  MonitorSmartphone,
  MapPin,
  TrendingUp,
  CalendarDays,
  Plus,
  Trash2,
  Users,
  Receipt,
  CheckCircle2,
  Mail,
  KeyRound,
  FileText,
  FileDown,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function FranchiseDashboard() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const hubParam = params.get("hub_id");
  const isHQView = user && user.role !== "franchise_owner";
  const query = isHQView && hubParam ? `?hub_id=${hubParam}` : "";

  const [data, setData] = useState(null);
  const [hubs, setHubs] = useState([]);

  const loadOverview = async () => {
    const { data } = await api.get(`/franchise/dashboard${query}`);
    setData(data);
  };
  useEffect(() => {
    if (isHQView) {
      (async () => {
        const r = await api.get("/hubs");
        setHubs(r.data);
      })();
    }
  }, [isHQView]);

  useEffect(() => {
    if (isHQView && !hubParam) return; // need to pick a hub first
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubParam]);

  if (isHQView && !hubParam) {
    return (
      <div className="space-y-6" data-testid="hq-pick-hub">
        <h1 className="text-3xl font-bold">Pick a franchise hub</h1>
        <div className="grid md:grid-cols-3 gap-4">
          {hubs.map((h) => (
            <a
              key={h.id}
              href={`/app/franchise?hub_id=${h.id}`}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04]"
            >
              <div className="text-xs uppercase tracking-[0.2em] text-[#E8D6A3]">
                {h.format}
              </div>
              <div className="text-lg font-bold mt-1">{h.name}</div>
              <div className="text-sm text-zinc-500">{h.city}</div>
            </a>
          ))}
        </div>
      </div>
    );
  }

  if (!data)
    return <div className="text-zinc-500">Loading franchise dashboard…</div>;

  const hub = data.hub;

  return (
    <div className="space-y-8" data-testid="franchise-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#E8D6A3] mb-3">
            {isHQView ? "HQ view · Franchise" : "Franchise owner"}
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
            {hub.name}.
          </h1>
          <div className="flex items-center gap-2 text-zinc-500 mt-2">
            <MapPin size={14} /> {hub.address}
            <Badge variant="outline" className="border-[#D4B878]/40 text-[#E8D6A3] ml-2">
              {hub.format}
            </Badge>
            {data.owner && (
              <span className="text-xs ml-2">
                · Owner: <span className="text-zinc-300">{data.owner.name}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white/5 border border-white/10 rounded-full p-1">
          <TabsTrigger value="overview" data-testid="fr-tab-overview" className="rounded-full">
            <TrendingUp size={14} className="mr-2" /> Overview
          </TabsTrigger>
          {!isHQView && (
            <>
              <TabsTrigger value="staff" data-testid="fr-tab-staff" className="rounded-full">
                <Users size={14} className="mr-2" /> Staff
              </TabsTrigger>
              <TabsTrigger value="royalty" data-testid="fr-tab-royalty" className="rounded-full">
                <Receipt size={14} className="mr-2" /> Royalty
              </TabsTrigger>
            </>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-8 space-y-8">
          <Overview data={data} />
        </TabsContent>

        {!isHQView && (
          <>
            <TabsContent value="staff" className="mt-8">
              <StaffTab />
            </TabsContent>
            <TabsContent value="royalty" className="mt-8">
              <RoyaltyTab />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}

function Overview({ data }) {
  const occ = data.total_seats
    ? Math.min(100, Math.round((data.active_now / data.total_seats) * 100))
    : 0;
  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          icon={<TrendingUp />}
          label="Revenue (today)"
          value={formatINR(data.today_revenue)}
          sub={`${formatINR(data.total_revenue)} all-time`}
          tone="gold"
        />
        <KPI
          icon={<CalendarDays />}
          label="Bookings (today)"
          value={data.today_bookings}
          sub={`${data.total_bookings} all-time`}
        />
        <KPI
          icon={<Activity />}
          label="Active now"
          value={`${data.active_now}`}
          sub={`${occ}% seat occupancy`}
          tone="gold"
        />
        <KPI
          icon={<Wrench />}
          label="Devices in maintenance"
          value={data.devices_in_maintenance}
          sub={`${data.total_devices} total`}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ChartCard title="Revenue by resource">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.revenue_by_type}>
              <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
              <XAxis dataKey="type" stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
              <YAxis stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "#14110E",
                  border: "1px solid #27272a",
                  borderRadius: 12,
                }}
              />
              <Bar dataKey="revenue" fill="#D4B878" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Peak hours">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.peak_hours}>
              <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
              <XAxis dataKey="hour" stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
              <YAxis stroke="#666" tick={{ fill: "#999", fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "#14110E",
                  border: "1px solid #27272a",
                  borderRadius: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#E8D6A3"
                strokeWidth={2.5}
                dot={{ fill: "#D4B878", r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <InfoCard icon={<Armchair />} label="Total seats" value={data.total_seats} sub={`Format ${data.hub.format}`} />
        <InfoCard icon={<MonitorSmartphone />} label="Total devices" value={data.total_devices} sub="Macs + PS5" />
        <InfoCard icon={<Activity />} label="Occupancy" value={`${occ}%`} sub="Active now / total seats" />
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/5 text-xs uppercase tracking-[0.22em] text-zinc-500">
          Recent bookings
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-zinc-500 uppercase text-[10px] tracking-[0.18em]">
            <tr>
              <th className="text-left p-4">User</th>
              <th className="text-left p-4">Resource</th>
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Time</th>
              <th className="text-left p-4">Price</th>
              <th className="text-left p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_bookings.map((b) => (
              <tr key={b.id} className="border-t border-white/5" data-testid={`franchise-booking-${b.id}`}>
                <td className="p-4 font-semibold">{b.user_name}</td>
                <td className="p-4 text-zinc-300">{b.resource_label}</td>
                <td className="p-4 text-zinc-400">{b.date}</td>
                <td className="p-4 text-zinc-400">
                  {formatHour(b.start_hour)}–{formatHour(b.start_hour + b.duration_hours)}
                </td>
                <td className="p-4 text-[#E8D6A3] font-bold">{formatINR(b.price)}</td>
                <td className="p-4">
                  <Badge variant="outline" className="border-white/10 capitalize">
                    {b.status.replace("_", " ")}
                  </Badge>
                </td>
              </tr>
            ))}
            {data.recent_bookings.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-zinc-500">
                  No bookings yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------- STAFF TAB ---------- */
function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/franchise/staff");
    setStaff(data);
  };
  useEffect(() => {
    load();
  }, []);

  const remove = async (uid) => {
    if (!window.confirm("Remove this staff member?")) return;
    try {
      await api.delete(`/franchise/staff/${uid}`);
      toast.success("Staff removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setRole = async (uid, role) => {
    try {
      await api.patch(`/franchise/staff/${uid}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [pwDialog, setPwDialog] = useState(null);

  const resetPw = async (s) => {
    try {
      const { data } = await api.post(`/franchise/staff/${s.id}/reset-password`);
      setPwDialog({ staff: s, password: data.temp_password, type: "reset" });
      toast.success(`Password reset · email sent to ${data.email_sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendInvite = async (s) => {
    try {
      const { data } = await api.post(`/franchise/staff/${s.id}/send-invite`);
      setPwDialog({ staff: s, password: data.temp_password, type: "invite" });
      toast.success(`Invite sent to ${data.email_sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="fr-staff-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Your staff</h2>
          <p className="text-sm text-zinc-500">
            Staff accounts at your hub. They can use the hub console & QR scanner.
          </p>
        </div>
        <AddStaffDialog open={open} setOpen={setOpen} onDone={load} />
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-zinc-500 uppercase text-[10px] tracking-[0.18em]">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Phone</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Joined</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && (
              <tr>
                <td colSpan={6} className="p-10 text-center text-zinc-500">
                  No staff yet. Add your first team member.
                </td>
              </tr>
            )}
            {staff.map((s) => (
              <tr key={s.id} className="border-t border-white/5" data-testid={`fr-staff-row-${s.id}`}>
                <td className="p-4 font-semibold">{s.name}</td>
                <td className="p-4 text-zinc-400">{s.email}</td>
                <td className="p-4 text-zinc-400">{s.phone || "—"}</td>
                <td className="p-4">
                  <Select defaultValue={s.role} onValueChange={(v) => setRole(s.id, v)}>
                    <SelectTrigger
                      data-testid={`fr-staff-role-${s.id}`}
                      className="bg-white/5 border-white/10 w-[140px] rounded-full"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4 text-zinc-500 text-xs">
                  {new Date(s.created_at).toLocaleDateString()}
                </td>
                <td className="p-4 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`fr-staff-menu-${s.id}`}
                        className="text-zinc-400 hover:text-white"
                      >
                        <MoreVertical size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="bg-[#14110E] border-white/10 text-white"
                    >
                      <DropdownMenuItem
                        onClick={() => sendInvite(s)}
                        data-testid={`fr-staff-invite-${s.id}`}
                        className="cursor-pointer"
                      >
                        <Mail size={14} className="mr-2" /> Resend invite
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => resetPw(s)}
                        data-testid={`fr-staff-reset-${s.id}`}
                        className="cursor-pointer"
                      >
                        <KeyRound size={14} className="mr-2" /> Reset password
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => remove(s.id)}
                        data-testid={`fr-staff-remove-${s.id}`}
                        className="cursor-pointer text-red-400 focus:text-red-400"
                      >
                        <Trash2 size={14} className="mr-2" /> Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PasswordRevealDialog info={pwDialog} onClose={() => setPwDialog(null)} />
    </div>
  );
}

function PasswordRevealDialog({ info, onClose }) {
  if (!info) return null;
  return (
    <Dialog open={!!info} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#14110E] border-[#D4B878]/40 text-white max-w-md" data-testid="fr-pw-dialog">
        <DialogHeader>
          <DialogTitle>
            {info.type === "reset" ? "Password reset" : "Invite sent"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
            <Mail size={18} className="text-emerald-400 mt-0.5 shrink-0" />
            <div className="text-sm text-zinc-300">
              Email delivered to{" "}
              <span className="font-bold text-white">{info.staff.email}</span>.
              <div className="text-[11px] text-zinc-500 mt-1">
                (Mocked email delivery · logged in <span className="text-[#E8D6A3]">email_log</span>)
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-2">
              Temporary password
            </div>
            <div
              className="rounded-xl border border-[#D4B878]/30 bg-[#1a1508] p-4 text-2xl font-black tracking-[0.2em] text-[#E8D6A3] text-center select-all"
              data-testid="fr-pw-dialog-password"
            >
              {info.password}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">
              Share this with {info.staff.name} — they should change it after first login.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              navigator.clipboard?.writeText(info.password);
              toast.success("Copied");
            }}
            variant="outline"
            data-testid="fr-pw-copy-btn"
            className="bg-white/5 border-white/10 rounded-full"
          >
            Copy
          </Button>
          <Button
            onClick={onClose}
            data-testid="fr-pw-close-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddStaffDialog({ open, setOpen, onDone }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "staff",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) =>
    setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/franchise/staff", form);
      toast.success("Staff added · invite email sent");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", role: "staff", password: "" });
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="fr-add-staff-btn"
          className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
        >
          <Plus size={16} className="mr-2" /> Add staff
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#14110E] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Add staff member</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name">
              <Input
                data-testid="fr-staff-name"
                value={form.name}
                onChange={set("name")}
                className="bg-white/5 border-white/10"
              />
            </Field>
            <Field label="Role">
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="fr-staff-role-select" className="bg-white/5 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Email">
            <Input
              data-testid="fr-staff-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              className="bg-white/5 border-white/10"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <Input
                data-testid="fr-staff-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91-"
                className="bg-white/5 border-white/10"
              />
            </Field>
            <Field label="Temporary password">
              <Input
                data-testid="fr-staff-password"
                type="text"
                minLength={6}
                value={form.password}
                onChange={set("password")}
                className="bg-white/5 border-white/10"
              />
            </Field>
          </div>
          <div className="text-[11px] text-zinc-500">
            An invite email with this password will be sent automatically. (Mocked delivery)
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={busy || !form.name || !form.email || !form.password}
            data-testid="fr-staff-create-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            {busy ? "Adding…" : "Add & invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- ROYALTY TAB ---------- */
function RoyaltyTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [emailing, setEmailing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/franchise/royalty");
    setData(data);
  };
  useEffect(() => {
    load();
  }, []);

  const markPaid = async (period) => {
    setBusy(period);
    try {
      await api.post(`/franchise/royalty/${period}/mark-paid`);
      toast.success(`Period ${period} marked paid`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const downloadFile = async (kind) => {
    try {
      const { data } = await api.get(`/franchise/royalty/export.${kind}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([data], { type: kind === "csv" ? "text/csv" : "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `mirrorloops-royalty.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/franchise/royalty/email");
      toast.success(`Emailed to ${data.to}${data.cc?.length ? ` · cc ${data.cc.join(", ")}` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEmailing(false);
    }
  };

  if (!data) return <div className="text-zinc-500">Loading royalty…</div>;

  return (
    <div className="space-y-6" data-testid="fr-royalty-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold tracking-tight">Royalty statement</h2>
        <div className="flex gap-2">
          <Button
            onClick={() => downloadFile("csv")}
            variant="outline"
            data-testid="fr-royalty-csv-btn"
            className="bg-white/5 border-white/10 rounded-full"
          >
            <FileDown size={14} className="mr-2" /> CSV
          </Button>
          <Button
            onClick={() => downloadFile("pdf")}
            variant="outline"
            data-testid="fr-royalty-pdf-btn"
            className="bg-white/5 border-white/10 rounded-full"
          >
            <FileText size={14} className="mr-2" /> PDF
          </Button>
          <Button
            onClick={emailReport}
            disabled={emailing}
            data-testid="fr-royalty-email-btn"
            className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
          >
            <Mail size={14} className="mr-2" />
            {emailing ? "Sending…" : "Email to HQ"}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <KPI
          icon={<Receipt />}
          label="Outstanding"
          value={formatINR(data.outstanding_total)}
          sub="Across pending periods"
          tone="gold"
        />
        <KPI
          icon={<TrendingUp />}
          label="Royalty rate"
          value={`${Math.round(data.royalty_rate * 100)}%`}
          sub="Of gross hub revenue"
        />
        <KPI
          icon={<CalendarDays />}
          label="Periods shown"
          value={data.statements.length}
          sub="Last 6 months"
        />
      </div>

      <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="fr-royalty-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-zinc-500 uppercase text-[10px] tracking-[0.18em]">
            <tr>
              <th className="text-left p-4">Period</th>
              <th className="text-left p-4">Bookings</th>
              <th className="text-left p-4">Gross revenue</th>
              <th className="text-left p-4">Rate</th>
              <th className="text-left p-4">Royalty due</th>
              <th className="text-left p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {data.statements.map((s) => (
              <tr key={s.period} className="border-t border-white/5" data-testid={`fr-royalty-row-${s.period}`}>
                <td className="p-4 font-bold">{s.period}</td>
                <td className="p-4 text-zinc-400">{s.bookings}</td>
                <td className="p-4 text-zinc-200">{formatINR(s.revenue)}</td>
                <td className="p-4 text-zinc-400">{Math.round(s.royalty_rate * 100)}%</td>
                <td className="p-4 text-[#E8D6A3] font-black">{formatINR(s.royalty_amount)}</td>
                <td className="p-4">
                  {s.status === "paid" ? (
                    <span className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] uppercase tracking-[0.18em] font-bold">
                      <CheckCircle2 size={12} /> paid
                    </span>
                  ) : (
                    <Badge
                      variant="outline"
                      className="border-[#D4B878]/40 text-[#E8D6A3] capitalize"
                    >
                      {s.status}
                    </Badge>
                  )}
                  {s.reference && (
                    <div className="text-[10px] text-zinc-500 mt-1">ref · {s.reference}</div>
                  )}
                </td>
                <td className="p-4 text-right">
                  {s.status !== "paid" && s.royalty_amount > 0 ? (
                    <Button
                      size="sm"
                      data-testid={`fr-royalty-pay-${s.period}`}
                      onClick={() => markPaid(s.period)}
                      disabled={busy === s.period}
                      className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full"
                    >
                      {busy === s.period ? "Processing…" : "Mark paid"}
                    </Button>
                  ) : (
                    <span className="text-zinc-600 text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-zinc-600">
        Royalty computed on gross non-cancelled booking revenue for the period.
        Payments use a mocked gateway in this build.
      </div>
    </div>
  );
}

/* ---------- shared ---------- */
function KPI({ icon, label, value, sub, tone }) {
  return (
    <div
      className={`rounded-2xl p-6 border ${
        tone === "gold"
          ? "border-[#D4B878]/30 bg-gradient-to-br from-[#1a1508] to-[#0c0c0c]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="p-2 rounded-lg bg-white/5 text-[#E8D6A3]">{icon}</div>
      </div>
      <div className="text-3xl font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
        {label}
      </div>
      {sub && <div className="text-xs text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}

function InfoCard({ icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex items-center gap-4">
      <div className="p-3 rounded-xl bg-[#D4B878]/10 text-[#E8D6A3]">{icon}</div>
      <div>
        <div className="text-2xl font-black">{value}</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          {label}
        </div>
        <div className="text-xs text-zinc-600 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-4">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
